import React, { FC, useMemo, useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, FormGroup, Label } from 'reactstrap';
import { ProductModel } from '../../../../../shared/orders';
import { ActionButton, ElementHeader, ElementSelector, MaterialIcon } from '../../../../components';
import { JSONForm } from '../../../../components/json-form/JSONForm';
import { useTranslation } from '../../../../translations';
import { displayName } from '../../../../utils';
import { LocalSearchProvider } from '../utils';

interface UpgradeInfoProps {
  readonly product: ProductModel;
  readonly onUpdate: () => void;
}

const enum UpgradeInfoMode {
  View,
  Edit,
}

export const UpgradeInfo: FC<UpgradeInfoProps> = ({ product, onUpdate }) => {
  const { ts } = useTranslation();
  const [mode, setMode] = useState(UpgradeInfoMode.View);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const searchProvider = useMemo(
    () => new LocalSearchProvider(async () => await api.getProducts()),
    [],
  );

  if (mode === UpgradeInfoMode.View) {
    return (
      <Card className="mt-10">
        <CardHeader>
          <div className="card-sub-header">{ts('upgrade_paths')}</div>
          {!product.deletedAt && (
            <div className="float-end">
              <Button
                color="success"
                id="edit"
                onClick={() => {
                  setMode(UpgradeInfoMode.Edit);
                }}
                title={ts('common.add')}
              >
                <MaterialIcon name="add" />
              </Button>
            </div>
          )}
        </CardHeader>
        <CardBody>
          {product.upgrades.length === 0 ? (
            <i>{ts('there_are_currently_no_upgrade')}</i>
          ) : (
            product.upgrades.map((u) => (
              <ElementHeader
                icon={{ iconName: 'arrow_upward' }}
                key={u.id}
                rightContent={
                  <ActionButton
                    action={async () => {
                      await api.deleteProductUpgrade(product.id, u.id);
                    }}
                    className="action-delete"
                    color="danger"
                    onSuccess={onUpdate}
                    outline
                    title={ts('common.delete')}
                  >
                    <MaterialIcon name="delete" />
                  </ActionButton>
                }
                title={displayName(u)}
              >
                <Badge color="success" pill>
                  {ts('upgrade')}
                </Badge>
              </ElementHeader>
            ))
          )}
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mt-10">
      <CardHeader>
        <div className="card-sub-header">{ts('upgrade_paths')}</div>
        <div className="float-end">
          <Button
            color="danger"
            id="cancel"
            onClick={() => {
              setMode(UpgradeInfoMode.View);
            }}
            title={ts('common.cancel')}
          >
            <MaterialIcon name="cancel" />
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        <JSONForm<undefined, { upgradeIds: number[] }>
          method="post"
          onSuccess={() => {
            onUpdate();
            setMode(UpgradeInfoMode.View);
          }}
          path={`/api/products/${product.id}/upgrades`}
          preSubmit={(r) => (r.inputs.upgradeIds = selectedIds)}
        >
          <FormGroup>
            <Label for="product">{ts('common.product_search')}</Label>
            <ElementSelector<ProductModel>
              id="upgradeProductSelector"
              searchProvider={searchProvider}
              selectionIdsChanged={setSelectedIds}
            />
          </FormGroup>
          <Button block color="primary">
            {ts('create_upgrades')}
          </Button>
        </JSONForm>
      </CardBody>
    </Card>
  );
};
